import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
// form modules
import { SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import axios from "axios";
import * as CSS from "csstype";
import dokodemo_logo from "../../logo-dokodemo.png";
import { API_URL } from "../../Const";
import { useLoadingOverlay } from "../../utilHooks/loadingOverlay";

import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@material-ui/core";

import { Button, Container, Stack, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// フォームの型
interface PasswordResetFormInput {
  email: string;
  confirmation_code: string;
  new_password: string;
  new_password_re: string;
}

// バリデーションルール
const schema = yup.object({
  email: yup
    .string()
    .required("必須項目です")
    .email("正しいメールアドレス形式で入力してください")
    .max(50, "50文字以内で入力してください。"),
  new_password: yup
    .string()
    .required("必須項目です")
    .min(8, "8文字以上入力してください")
    .max(30, "30文字以下で入力してください")
    .matches(/[a-zA-Z]/, "英字を含めてください")
    .matches(/[0-9]/, "数字を含めてください")
    .matches(/^[\x20-\x7e]*$/, "半角文字で入力してください。"),
  new_password_re: yup
    .string()
    .required("必須項目です")
    .min(8, "8文字以上入力してください")
    .max(30, "30文字以下で入力してください")
    .matches(/[a-zA-Z]/, "英字を含めてください")
    .matches(/[0-9]/, "数字を含めてください")
    .matches(/^[\x20-\x7e]*$/, "半角文字で入力してください。")
    .oneOf([yup.ref("new_password"), null], "入力したパスワードが一致しません"),
});

function App() {
  const { openLoading, closeLoading } = useLoadingOverlay();
  //GETパラメータ取得
  const search = useLocation().search;
  const query = new URLSearchParams(search);

  const titleStyle = {
    fontSize: "40px",
    fontWeight: "700",
    color: "#e15f5f",
    margin: "0",
  };
  const subTitleStyle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#646464",
    margin: "10px",
  };
  const msgStyle: CSS.Properties = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#0000cd",
    margin: "10px",
    textAlign: "center",
  };
  const alertMsgStyle: CSS.Properties = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#ff6347",
    margin: "10px",
    textAlign: "center",
  };

  const buttonStyle = {
    color: "#fff",
    backgroundColor: "#1565c0",
    padding: "8px 22px",
    border: 0,
    borderRadius: "4px",
  };

  const getState = (state: any, name: string): string =>
    (state as any)?.[name] ?? "";
  const history = useHistory();
  const err = getState(history.location.state, "err");
  const [msg1, setMsg1] = useState("");
  const [msg2, setMsg2] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordRe, setShowNewPasswordRe] = useState(false);

  // コード送信
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetFormInput>({
    resolver: yupResolver(schema),
  });

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowNewPasswordRe = () => {
    setShowNewPasswordRe(!showNewPasswordRe);
  };

  // フォーム送信時の処理
  const onSubmit: SubmitHandler<PasswordResetFormInput> = (data) => {
    openLoading();
    axios
      .post(API_URL.USER_PASS_RESET, {
        user_id: data.email,
        new_password: data.new_password,
        new_password_re: data.new_password_re,
        confirmation_code: data.confirmation_code,
      })
      .then((res) => {
        setMsg2("");
        setMsg1(res.data.data);
        history.push("/PasswordResetComplete");
      })
      .catch((error) => {
        console.log(error.response.data.errMessage);
        if (error.response.data.errMessage.includes("ExpiredCodeException")) {
          setMsg1("");
          setMsg2(
            "パスワードリセットの有効期限が切れました。再度パスワードをリセットしてください。"
          );
        } else {
          setMsg1("");
          setMsg2("登録されているメールアドレスと一致しません。");
        }
      })
      .finally(() => {
        closeLoading();
      });
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 5 }}>
      {/* <Typography variant="body2" color="textSecondary" align="center"> */}

      {/* </Typography> */}
      <Stack spacing={3} margin={5}>
        <img src={dokodemo_logo} alt="logo" />
        <p style={subTitleStyle}>パスワードリセット</p>
        <input
          type="hidden"
          {...register("confirmation_code")}
          value={query.get("confirmation_code")?.toString()}
        />
        <TextField
          required
          label="メールアドレス"
          type="email"
          {...register("email")}
          error={"email" in errors}
          helperText={errors.email?.message}
          inputProps={{ maxLength: 30 }}
        />
        {/* <TextField
          required
          label="検証コード"
          {...register('confirmation_code')}
          error={'confirmation_code' in errors}
          helperText={errors.confirmation_code?.message}
        /> */}
        {/* <TextField
          required
          label="新パスワード"
          type="password"
          {...register('new_password')}
          error={'new_password' in errors}
          helperText={errors.new_password?.message}
        /> */}
        <FormControl variant="outlined" color="primary">
          <InputLabel
            htmlFor="password"
            required
            error={"new_password" in errors}
          >
            新パスワード
          </InputLabel>
          <OutlinedInput
            required
            color="primary"
            id="new_password"
            type={showNewPassword ? "text" : "password"}
            error={"new_password" in errors}
            inputProps={{ maxLength: 30 }}
            autoComplete="off"
            {...register("new_password")}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowNewPassword}
                  edge="end"
                >
                  {showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label="新パスワード"
          />
          <FormHelperText error id="password-error">
            {errors.new_password?.message}
          </FormHelperText>
          ※半角英数字のみ(大文字記号可)
          <br />
          ※8文字以上30文字以内
        </FormControl>
        {/* <TextField
          required
          label="新パスワード(再)"
          type="password"
          {...register('new_password_re')}
          error={'new_password_re' in errors}
          helperText={errors.new_password_re?.message}
        /> */}
        <FormControl variant="outlined" color="primary">
          <InputLabel
            htmlFor="password"
            required
            error={"new_password_re" in errors}
          >
            新パスワード(再)
          </InputLabel>
          <OutlinedInput
            required
            color="primary"
            id="password-re"
            type={showNewPasswordRe ? "text" : "password"}
            error={"new_password_re" in errors}
            inputProps={{ maxLength: 30 }}
            autoComplete="off"
            {...register("new_password_re")}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowNewPasswordRe}
                  edge="end"
                >
                  {showNewPasswordRe ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label="新パスワード(再)"
          />
          <FormHelperText error id="password-re-error">
            {errors.new_password_re?.message}
          </FormHelperText>
          ※半角英数字のみ(大文字記号可)
          <br />
          ※8文字以上30文字以内
        </FormControl>
        {/*
        <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="ログイン情報を保存"
        />
        */}
        <Button
          style={buttonStyle}
          color="primary"
          variant="contained"
          size="large"
          onClick={handleSubmit(onSubmit)}
        >
          パスワードリセット
        </Button>
        <p style={msgStyle}>{msg1}</p>
        <p style={alertMsgStyle}>{msg2}</p>
      </Stack>
    </Container>
  );
}

export default App;
